import { makeObservable, observable, action, computed } from 'mobx';
import lottie, { type AnimationItem, type RendererType } from 'lottie-web';

export default class AnimationPlayerStore {
    progress: number = 0;
    durationSeconds: number = 0;
    currentTimeSeconds: number = 0;
    instance: AnimationItem | null = null; // reference to the lottie-web instance
    isPlaying: boolean = false;
    showFrames: boolean = false;
    showTimeWithFrames: boolean = false;
    currentFrame = NaN;

    loop = true;
    autoplay = true;
    renderer: RendererType = 'svg';
    scale = 1;
    minScale = 0.05;
    maxScale = 5;
    scaleSteps = [0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5];
    scaleStepIndex: number;
    speed = 1;

    speedOptions = [2, 1, 0.5, 0.25];

    constructor() {
        // Initialize scaleStepIndex to the index of 1 in scaleSteps
        this.scaleStepIndex = this.scaleSteps.indexOf(1);
        if (this.scaleStepIndex === -1) {
            // If 1 is not in scaleSteps, default to closest to the number 1
            this.scaleStepIndex = this.scaleSteps.reduce((prev, curr) => {
                return (Math.abs(curr - 1) < Math.abs(prev - 1) ? curr : prev);
            });
        }

        if (this.autoplay) {
            this.setIsPlaying(true);
        }

        makeObservable(this, {
            progress: observable,
            durationSeconds: observable,
            currentTimeSeconds: observable,
            instance: observable,
            currentTimeMinutes: computed,
            durationMinutes: computed,
            isPlaying: observable,
            currentFrame: observable,
            loop: observable,
            autoplay: observable,
            renderer: observable,
            speed: observable,
            updateProgress: action,
            updateAnimationDuration: action,
            updateCurrentTime: action,
            updateAnimationInstance: action,
            updateCurrentFrame: action,
            setIsPlaying: action,
            setLoop: action,
            setRenderer: action,
            setConfig: action,
            togglePlayPause: action,
            play: action,
            pause: action,
            showFrames: observable,
            showTimeWithFrames: observable,
            toggleTimeDisplay: action,
            currentTimeDisplay: computed,
            durationDisplay: computed,
            progressInFrames: computed,
            setProgressInFrames: action,
            currentTimeWithFrames: computed,
            scale: observable,
            setScale: action,
            increaseScale: action,
            decreaseScale: action,
            scalePercentage: computed,
            handleAnimationComplete: action,
            handleEnterFrame: action,          // Added
            handleDataReady: action,           // Added
            handleDOMLoaded: action,           // Added
        });
    }

    get progressInFrames(): number {
        return Math.round(this.currentFrame);
    }

    get totalFrames() {
        const animation = this.instance;
        if (animation) {
            return animation.totalFrames;
        } else {
            return NaN;
        }
    }

    get currentTimeWithFrames(): string {
        const animation = this.instance;
        if (!animation) return "0:00:00";

        const totalSeconds = Math.floor(this.currentTimeSeconds);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const frames = Math.round((this.currentTimeSeconds - totalSeconds) * animation.frameRate);

        return `${minutes}:${seconds.toString().padStart(2, '0')}:${frames.toString().padStart(2, '0')}`;
    }

    loadAnimation = (container: HTMLDivElement, url: string) => {
        if(this.instance) {
            this.destroy();
        }
        const animation = lottie.loadAnimation({
            container: container,
            renderer: this.renderer,
            loop: this.loop,
            autoplay: this.autoplay,
            path: url
        });
        this.setAnimationContainer(container);
        this.updateAnimationInstance(animation);
    }

    setProgressInFrames = (frame: number) => {
        const animation = this.instance;
        if (animation) {
            this.currentFrame = frame;
            animation.goToAndStop(frame, true);
            this.updateCurrentTime(frame / animation.frameRate);
            this.updateProgress((frame / animation.totalFrames) * 100);
        }
    }

    toggleTimeDisplay = () => {
        this.showFrames = !this.showFrames;
        console.log('toggleTimeDisplay', this.showFrames);
    }

    toggleLoop = () => {
        if (!this.instance) return;
        this.loop = !this.loop;
        this.instance.loop = this.loop;
    }

    setLoop = (loop: boolean) => {
        if (!this.instance) return;
        this.loop = loop;
        this.instance.loop = loop;
    }

    get currentTimeDisplay(): string {
        if (this.showFrames) {
            return Math.round(this.currentFrame).toString();
        } else {
            return this.currentTimeWithFrames;
        }
        // } else if (this.showTimeWithFrames) { // Add this new condition
        //     return this.currentTimeWithFrames;
        // } else {
        //     return this.currentTimeMinutes;
        // }
    }

    get durationDisplay(): string {
        if (this.showFrames) {
            return Math.round(this.totalFrames).toString();
        } else {
            return this.durationMinutes;
        }
    }

    togglePlayPause = () => {
        const instance = this.instance;
        if (instance) {
            if (instance.isPaused) {
                this.play();
            } else {
                this.pause();
            }
        }
    };

    play = () => {
        if (!this.instance) return;
        if (this.currentFrame >= this.instance.totalFrames) {
            this.setProgressInFrames(0);
        }
        this.instance.play();
        this.setIsPlaying(true);
    }

    pause = () => {
        if (!this.instance) return;
        this.instance.pause();
        this.setIsPlaying(false);
    }

    setConfig = (config: { loop?: boolean, autoplay?: boolean, renderer?: RendererType }) => {
        if (config.loop !== undefined) {
            this.loop = config.loop;
        }
        if (config.autoplay !== undefined) {
            this.autoplay = config.autoplay;
        }
        if (config.renderer !== undefined) {
            this.renderer = config.renderer;
        }
    }

    setIsPlaying = (isPlaying: boolean) => {
        this.isPlaying = isPlaying;
    }

    setRenderer = (renderer: RendererType) => {
        if (this.instance) this.instance.renderer(renderer);
        this.renderer = renderer;
    }

    setSpeed = (speed: number) => {
        if (this.instance) {
            this.instance.setSpeed(speed);
            this.speed = speed;
        }
    }

    setScale = (scale: number) => {
        // Ensure the scale matches one of the scaleSteps
        const index = this.scaleSteps.indexOf(scale);
        if (index !== -1) {
            this.scaleStepIndex = index;
            this.scale = scale;
            if (this.instance) {
                this.instance.resize();
            }
        }
    }

    increaseScale = () => {
        if (this.scaleStepIndex < this.scaleSteps.length - 1) {
            this.scaleStepIndex += 1;
            this.scale = this.scaleSteps[this.scaleStepIndex];
            if (this.instance) {
                this.instance.resize();
            }
        }
    }

    decreaseScale = () => {
        if (this.scaleStepIndex > 0) {
            this.scaleStepIndex -= 1;
            this.scale = this.scaleSteps[this.scaleStepIndex];
            if (this.instance) {
                this.instance.resize();
            }
        }
    }

    get scalePercentage(): string {
        return `${Math.round(this.scale * 100)}%`;
    }

    updateCurrentFrame = (frame: number) => {
        this.currentFrame = frame;
    }

    updateProgress = (progress: number) => {
        this.progress = progress;
    }

    updateAnimationDuration = (duration: number) => {
        this.durationSeconds = duration;
    }

    updateCurrentTime = (time: number) => {
        this.currentTimeSeconds = time;
    }

    /**
     * Updates the animation instance and sets up event listeners.
     * @param instance Reference to the lottie-web instance or null
     */
    updateAnimationInstance = (instance: AnimationItem) => {
        this.instance = instance;

        if (this.instance) {
            // Add event listeners for animation events
            this.instance.addEventListener('complete', this.handleAnimationComplete);
            this.instance.addEventListener('enterFrame', this.handleEnterFrame);
            this.instance.addEventListener('DOMLoaded', this.handleDOMLoaded);
            this.instance.addEventListener('data_ready', this.handleDataReady);

            // Set initial loop state
            this.instance.loop = this.loop;
            this.instance.setSpeed(this.speed);
        }
    }

    /**
     * Handler for the animation 'complete' event.
     * Sets isPlaying to false when animation completes.
     */
    handleAnimationComplete = () => {
        this.setIsPlaying(false);
        this.setProgressInFrames(this.totalFrames);
    }

    /**
     * Handler for the animation 'enterFrame' event.
     * Updates progress, current time, and current frame.
     */
    handleEnterFrame = () => {
        const animation = this.instance;
        if (animation) {
            const progress = (animation.currentFrame / animation.totalFrames) * 100;
            const currentTime = (animation.currentFrame / animation.frameRate);
            this.updateProgress(progress);
            this.updateCurrentTime(currentTime);
            this.updateCurrentFrame(animation.currentFrame);
        }
    }

    /**
     * Handler for the animation 'data_ready' event, which is 
     * fired when the animation is loaded and data is ready.
     * Updates the duration of the animation.
     */
    handleDataReady = () => {
        const animation = this.instance;
        if (animation) {
            const durationInSeconds = animation.totalFrames / animation.frameRate;
            this.updateAnimationDuration(durationInSeconds);
        }
    }

    /**
     * Handler for the animation 'DOMLoaded' event.
     * Sets the size of the SVG element based on the viewBox or bounding box.
     */
    handleDOMLoaded = () => {
        const animation = this.instance;
        if (animation && this._animationContainerRef) {
            const animElement = this._animationContainerRef.firstElementChild as SVGSVGElement;
            if (animElement && animElement instanceof SVGSVGElement) {
                const viewBox = animElement.getAttribute('viewBox');
                if (viewBox) {
                    const [, , width, height] = viewBox.split(' ').map(Number);
                    if (!isNaN(width) && !isNaN(height)) {
                        animElement.style.width = `${width}px`;
                        animElement.style.height = `${height}px`;
                    }
                } else {
                    // Fallback if viewBox is not available
                    const bbox = animElement.getBBox();
                    animElement.style.width = `${bbox.width}px`;
                    animElement.style.height = `${bbox.height}px`;
                }
            }
        }
    }

    // To hold a reference to the animation container for DOMLoaded handling
    private _animationContainerRef: HTMLDivElement | null = null;

    /**
     * Sets the animation container reference used in handleDOMLoaded.
     * @param container The HTMLDivElement container.
     */
    setAnimationContainer = (container: HTMLDivElement | null) => {
        this._animationContainerRef = container;
    }

    get currentTimeMinutes(): string {
        const minutes = Math.floor(this.currentTimeSeconds / 60);
        const seconds = Math.floor(this.currentTimeSeconds % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    get durationMinutes(): string {
        const minutes = Math.floor(this.durationSeconds / 60);
        const seconds = Math.floor(this.durationSeconds % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    destroy = () => {
        this.reset();
    }

    reset = () => {
        // Reset progress and time-related properties
        // Reset progress and time-related properties
        if (this.instance) {
            this.instance.destroy();
            this.instance = null;
        }
        this.updateProgress(0);
        this.updateCurrentTime(0);
        this.updateCurrentFrame(0);
        this.setAnimationContainer(null);
        // Reset playback state
        this.updateAnimationDuration(0);

        this.setIsPlaying(false);

        // Reset display options
        this.setLoop(true);

        this.showFrames = false;
        // Reset scale to 1
        this.showTimeWithFrames = false;

        if (this.speed !== 1) {
            this.setSpeed(1);
        }

        this.scaleStepIndex = this.scaleSteps.indexOf(1);
        this.scale = this.scaleSteps[this.scaleStepIndex];
        console.log('reset: instance', this.instance);
    }

    /**
     * Converts time in seconds to a "seconds:frames" format.
     * @param timeSeconds The time in seconds to convert.
     * @returns A string formatted as "seconds:frames".
     */
    convertSecondsToSecondsFrames(timeSeconds: number): string {
        if (!this.instance) {
            return `0:00`;
        }
        const frameRate = this.instance.frameRate;
        const seconds = Math.floor(timeSeconds);
        const fractional = timeSeconds - seconds;
        const frames = Math.round(fractional * frameRate);
        return `${seconds}:${frames.toString().padStart(2, '0')}`;
    }
}